import { Button, ActionIcon, Menu } from '@mantine/core';
import { MoreHorizontal } from 'lucide-react';
import type { HealthAlert, AlertTier } from '../../../../types';
import classes from './CohortHealthSummary.module.css';

interface HealthAlertCardProps {
  alert: HealthAlert;
  tier: AlertTier;
  onDismiss?: (alert: HealthAlert) => void;
  onAddress?: (alert: HealthAlert) => void;
  onAddTask?: (alert: HealthAlert) => void;
  onViewEntity?: (alert: HealthAlert) => void;
  onDismissImprovement?: (alert: HealthAlert) => void;
  onRestore?: (alert: HealthAlert) => void;
  onViewNotes?: (alert: HealthAlert) => void;
}

export function HealthAlertCard({ alert, tier, onDismiss, onAddress, onAddTask, onViewEntity, onDismissImprovement, onRestore, onViewNotes }: HealthAlertCardProps) {
  const isOnTrack = tier === 'on_track';
  const isNotification = tier === 'notification';
  const isImprovement = alert.isImprovement === true;

  return (
    <div className={`${classes.alertCard} ${alert.isDismissed ? classes.dismissedAlert : ''}`}>
      <div className={classes.statBadge}>
        <span className={isImprovement ? classes.statValueImprovement : classes.statValue}>
          {alert.statValue}
        </span>
        <span className={isImprovement ? classes.statLabelImprovement : classes.statLabel}>
          {alert.statLabel}
        </span>
      </div>

      <div className={classes.alertContent}>
        <div className={classes.alertTitle}>
          {alert.title} <span className={classes.alertTitleSeparator}>•</span> {alert.description}
          {alert.isDismissed && alert.resolutionType && (
            <span
              className={`${classes.resolutionPill} ${
                alert.resolutionType === 'addressed'
                  ? classes.resolutionPillAddressed
                  : classes.resolutionPillDismissed
              }`}
              style={{ marginLeft: 8 }}
            >
              {alert.resolutionType === 'addressed' ? 'Addressed' : 'Dismissed'}
            </span>
          )}
        </div>
        {!alert.isDismissed && !isOnTrack && !isNotification && alert.suggestedAction && (
          <div className={classes.alertRecommendation}>
            Recommended action: {alert.suggestedAction}
          </div>
        )}
      </div>

      {alert.isDismissed && (onRestore || onViewNotes) && (
        <div className={`${classes.alertActions} ${classes.dismissedMenu}`}>
          <Menu position="bottom-end" withArrow>
            <Menu.Target>
              <ActionIcon variant="subtle" color="gray" size="lg">
                <MoreHorizontal size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {onRestore && (
                <Menu.Item onClick={() => onRestore(alert)}>Restore</Menu.Item>
              )}
              {onViewNotes && (
                <Menu.Item onClick={() => onViewNotes(alert)}>View Notes</Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </div>
      )}

      {!alert.isDismissed && (
        <div className={classes.alertActions}>
          {!isOnTrack && !isNotification && onAddTask && (
            <Button
              size="xs"
              variant="outline"
              color="dark"
              onClick={() => onAddTask(alert)}
            >
              Add a Task
            </Button>
          )}
          {onViewEntity && (
            <Button
              size="xs"
              variant="filled"
              onClick={() => onViewEntity(alert)}
            >
              {isNotification
                ? alert.suggestedAction
                : isImprovement
                  ? 'View Report'
                  : isOnTrack
                    ? 'View Details \u2192'
                    : isCoachDissatisfactionRule(alert.ruleKey)
                      ? 'Review Feedback'
                      : `View ${capitalize(alert.entityType)}`}
            </Button>
          )}
          {isImprovement && onDismissImprovement && (
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="lg">
                  <MoreHorizontal size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onDismissImprovement(alert)}>Dismiss</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
          {!isOnTrack && !isNotification && onDismiss && (
            <Menu position="bottom-end" withArrow>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" size="lg">
                  <MoreHorizontal size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => onDismiss(alert)}>Dismiss</Menu.Item>
                {onAddress && (
                  <Menu.Item onClick={() => onAddress(alert)}>Mark as Addressed</Menu.Item>
                )}
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
      )}
    </div>
  );
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** MED-488: Coach dissatisfaction rules use suggestedAction as CTA text */
function isCoachDissatisfactionRule(ruleKey: string): boolean {
  return ruleKey === 'midpoint_coach_dissatisfied' || ruleKey === 'endpoint_coach_dissatisfied';
}
